(function () {
  'use strict';

  if (window.customElements.get('faq-predictive-search')) {
    return;
  }

  class FaqPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        return;
      }

      this.form = this.querySelector('[data-faq-search-form]');
      this.input = this.querySelector('[data-faq-search-input]');
      this.clearButton = this.querySelector('[data-faq-search-clear]');
      this.results = this.querySelector('[data-faq-search-results]');
      this.status = this.querySelector('[data-faq-search-status]');
      this.cache = new Map();
      this.abortController = null;
      this.searchTimeout = null;
      this.lastRenderedTerm = '';

      if (!this.form || !this.input || !this.clearButton || !this.results || !this.status) {
        return;
      }

      this.handleInput = this.onInput.bind(this);
      this.handleClear = this.clearSearch.bind(this);
      this.handleSubmit = this.onSubmit.bind(this);
      this.handleKeydown = this.onKeydown.bind(this);
      this.handleDocumentClick = this.onDocumentClick.bind(this);

      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('keydown', this.handleKeydown);
      this.clearButton.addEventListener('click', this.handleClear);
      this.form.addEventListener('submit', this.handleSubmit);
      document.addEventListener('click', this.handleDocumentClick);
      this.dataset.initialized = 'true';
    }

    disconnectedCallback() {
      if (this.input) {
        this.input.removeEventListener('input', this.handleInput);
        this.input.removeEventListener('keydown', this.handleKeydown);
      }

      if (this.clearButton) {
        this.clearButton.removeEventListener('click', this.handleClear);
      }

      if (this.form) {
        this.form.removeEventListener('submit', this.handleSubmit);
      }

      document.removeEventListener('click', this.handleDocumentClick);
      window.clearTimeout(this.searchTimeout);

      if (this.abortController) {
        this.abortController.abort();
      }

      delete this.dataset.initialized;
    }

    getSearchTerm() {
      return this.input.value.trim().replace(/\s+/g, ' ');
    }

    onInput() {
      const term = this.getSearchTerm();
      this.clearButton.hidden = term.length === 0;
      window.clearTimeout(this.searchTimeout);

      if (term.length < 2) {
        if (this.abortController) {
          this.abortController.abort();
        }
        this.lastRenderedTerm = '';
        this.closeResults();
        this.results.innerHTML = '';
        this.status.textContent = term.length === 1 ? 'Enter at least two characters.' : '';
        return;
      }

      this.searchTimeout = window.setTimeout(() => this.getSearchResults(term), 250);
    }

    onSubmit(event) {
      const term = this.getSearchTerm();

      if (term.length < 2) {
        event.preventDefault();
        this.status.textContent = 'Enter at least two characters.';
        this.input.focus();
        return;
      }

      event.preventDefault();

      if (this.lastRenderedTerm !== term) {
        this.getSearchResults(term);
        return;
      }

      const firstResult = this.results.querySelector('a');
      if (firstResult) {
        firstResult.focus();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.closeResults();
        this.input.focus();
        return;
      }

      if (event.key === 'ArrowDown' && !this.results.hidden) {
        const firstResult = this.results.querySelector('a');
        if (firstResult) {
          event.preventDefault();
          firstResult.focus();
        }
      }
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.closeResults();
      }
    }

    clearSearch() {
      window.clearTimeout(this.searchTimeout);
      if (this.abortController) {
        this.abortController.abort();
      }
      this.input.value = '';
      this.clearButton.hidden = true;
      this.results.innerHTML = '';
      this.status.textContent = '';
      this.lastRenderedTerm = '';
      this.closeResults();
      this.input.focus();
    }

    openResults() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    closeResults() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.results.removeAttribute('aria-busy');
    }

    setLoading() {
      this.results.setAttribute('aria-busy', 'true');
      this.results.innerHTML = '<div class="faq-search-results__loading"><span class="faq-search-results__spinner" aria-hidden="true"></span><span>' + this.escapeHtml(this.dataset.loadingText || 'Searching questions...') + '</span></div>';
      this.status.textContent = this.dataset.loadingText || 'Searching questions...';
      this.openResults();
    }

    buildSearchUrl(term) {
      const url = new URL(this.dataset.searchUrl, window.location.origin);
      const scopedTerm = [this.dataset.scopeTerm, term].filter(Boolean).join(' ');

      url.searchParams.set('q', scopedTerm);
      url.searchParams.set('resources[type]', 'article');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][fields]', 'title,body,tag');
      url.searchParams.set('section_id', this.dataset.sectionId);

      return url.toString();
    }

    async getSearchResults(term) {
      const normalizedTerm = term.toLowerCase();

      if (this.cache.has(normalizedTerm)) {
        this.renderResults(this.cache.get(normalizedTerm), term);
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      this.setLoading();

      try {
        const response = await fetch(this.buildSearchUrl(term), {
          signal: this.abortController.signal,
          headers: {
            Accept: 'text/html'
          }
        });

        if (!response.ok) {
          throw new Error('FAQ predictive search request failed with status ' + response.status);
        }

        const responseText = await response.text();
        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const section = responseDocument.querySelector('#shopify-section-' + this.dataset.sectionId);

        if (!section) {
          throw new Error('FAQ predictive search section was not returned.');
        }

        const markup = section.innerHTML;
        this.cache.set(normalizedTerm, markup);

        if (this.getSearchTerm() === term) {
          this.renderResults(markup, term);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        this.results.removeAttribute('aria-busy');
        this.results.innerHTML = '<div class="faq-search-results__empty"><strong>' + this.escapeHtml(this.dataset.errorText || 'We could not load suggestions. Please try again.') + '</strong></div>';
        this.status.textContent = this.dataset.errorText || 'We could not load suggestions. Please try again.';
        this.openResults();
      }
    }

    renderResults(markup, term) {
      this.results.innerHTML = markup;
      this.results.removeAttribute('aria-busy');
      this.lastRenderedTerm = term;

      const resultRoot = this.results.querySelector('[data-faq-result-count]');
      const resultCount = resultRoot ? parseInt(resultRoot.dataset.faqResultCount, 10) || 0 : 0;
      this.status.textContent = resultCount === 1 ? '1 suggested answer.' : resultCount + ' suggested answers.';
      this.openResults();
    }

    escapeHtml(value) {
      const element = document.createElement('div');
      element.textContent = value;
      return element.innerHTML;
    }
  }

  window.customElements.define('faq-predictive-search', FaqPredictiveSearch);
})();
